import type { ZodType } from "@core/zod";
import { z } from "@core/zod";

import type { Promisable } from "../promise/index.ts";

import { generateToken, hashToken, verifyToken } from "./token.ts";

interface CreateSecretKeyParams<P extends string> {
  readonly keyPrefix: P;
}

interface CreateSecretKeyResult<P extends string> {
  readonly generateKey: () => Promisable<{
    readonly hash: string;
    readonly key: `${P}${string}`;
  }>;
  readonly keySchema: ZodType<`${P}${string}`, `${P}${string}`>;
  readonly verifyKey: (
    key: `${P}${string}`,
    hash: string,
  ) => Promisable<boolean>;
}

export function createSecretKey<P extends string>({
  keyPrefix,
}: CreateSecretKeyParams<P>): CreateSecretKeyResult<P> {
  return {
    generateKey: async () => {
      const key: `${P}${string}` = `${keyPrefix}${generateToken()}`;

      const hash = await hashToken(key);

      return { hash, key };
    },
    keySchema: z.safeId().startsWith(keyPrefix),
    verifyKey: async (key, hash) => verifyToken(key, hash),
  };
}
